import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getOppTradeVolume, type OppTradeVolumeResponse } from '@/services/oppDetectorApi';
import { labelToManualTradingExchange } from '@/services/manualTradingExchange';

const REFRESH_MS = 60_000;

export type TradeVolumeLegState = {
  data: OppTradeVolumeResponse | null;
  loading: boolean;
  error: string | null;
};

type TradeVolumeContextValue = {
  leg1: TradeVolumeLegState;
  leg2: TradeVolumeLegState;
  updatedAtMs: number | null;
};

const TradeVolumeContext = createContext<TradeVolumeContextValue | null>(null);

export function useTradeVolumeContext(): TradeVolumeContextValue {
  const ctx = useContext(TradeVolumeContext);
  if (!ctx) {
    throw new Error('useTradeVolumeContext must be used within TradeVolumeProvider');
  }
  return ctx;
}

type ProviderProps = {
  enabled: boolean;
  exchange1: string;
  exchange2: string;
  exchange1InstId: string;
  exchange2InstId: string;
  children: ReactNode;
};

const emptyLeg = (): TradeVolumeLegState => ({ data: null, loading: false, error: null });

export function TradeVolumeProvider({
  enabled,
  exchange1,
  exchange2,
  exchange1InstId,
  exchange2InstId,
  children,
}: ProviderProps) {
  const [leg1, setLeg1] = useState<TradeVolumeLegState>(emptyLeg);
  const [leg2, setLeg2] = useState<TradeVolumeLegState>(emptyLeg);
  const [updatedAtMs, setUpdatedAtMs] = useState<number | null>(null);

  const ex1 = labelToManualTradingExchange(exchange1);
  const ex2 = labelToManualTradingExchange(exchange2);
  const inst1 = exchange1InstId.trim();
  const inst2 = exchange2InstId.trim();
  const canFetch = enabled && !!ex1 && !!ex2 && !!inst1 && !!inst2;

  useEffect(() => {
    if (!canFetch || !ex1 || !ex2) {
      setLeg1(emptyLeg());
      setLeg2(emptyLeg());
      setUpdatedAtMs(null);
      return;
    }

    let cancelled = false;

    const load = async (showLoading: boolean) => {
      if (showLoading) {
        setLeg1((prev) => ({ ...prev, loading: true }));
        setLeg2((prev) => ({ ...prev, loading: true }));
      }

      const [result1, result2] = await Promise.allSettled([
        getOppTradeVolume(ex1, inst1),
        getOppTradeVolume(ex2, inst2),
      ]);

      if (cancelled) return;

      setLeg1({
        data: result1.status === 'fulfilled' ? result1.value : null,
        loading: false,
        error: result1.status === 'rejected'
          ? (result1.reason instanceof Error ? result1.reason.message : String(result1.reason))
          : null,
      });
      setLeg2({
        data: result2.status === 'fulfilled' ? result2.value : null,
        loading: false,
        error: result2.status === 'rejected'
          ? (result2.reason instanceof Error ? result2.reason.message : String(result2.reason))
          : null,
      });

      if (result1.status === 'fulfilled' || result2.status === 'fulfilled') {
        setUpdatedAtMs(Date.now());
      }
    };

    void load(true);
    const timer = window.setInterval(() => void load(false), REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [canFetch, ex1, ex2, inst1, inst2]);

  const value = useMemo(
    () => ({ leg1, leg2, updatedAtMs }),
    [leg1, leg2, updatedAtMs],
  );

  return (
    <TradeVolumeContext.Provider value={value}>
      {children}
    </TradeVolumeContext.Provider>
  );
}
